import { ReactNode, useEffect, useRef, useState } from "react";
import {
  BRACKET_INNER,
  PLACEHOLDER_INNER,
  SCRIPT_TOKEN_REGEX,
  Segment,
  currentSegmentIdx,
  emojify,
} from "../utils";

type Phase = "ready" | "countdown" | "recording";

interface Props {
  /** Lines shown before recording (above `---` in the script). */
  intro: string[];
  /** Recording-time segments (below `---`, or all lines if no separator). */
  segments: Segment[];
  /** Per-segment timing computed from durations + maxSec. */
  timing: { start: number; dur: number }[];
  /** Elapsed seconds in the recording (0 before REC starts). */
  elapsedSec: number;
  /** Current recorder phase — drives intro vs segments rendering. */
  phase: Phase;
}

const LEMONADE = "#FFFC7D";

/**
 * Segment-based teleprompter.
 *
 * Inline syntax (per line):
 * - `...(text)...` → lemonade-yellow placeholder.
 * - `[text]`       → orange highlight.
 * - Emoticons (`:)`, `;)`, `:p`, `XD`, `<3`, etc.) → unicode emoji.
 *
 * Segment markers (start of line):
 * - `(N)` or `(N.M)` → explicit duration in seconds.
 * - `()`            → auto duration (share remaining time by word count).
 * - `*(N)` / `*()`  → karaoke mode: same text rendered all at once, with a
 *                     progressive per-word lemonade underline that paces the
 *                     reading. Pondered by character count so long words get
 *                     more time than short ones.
 */
export default function Teleprompter({
  intro,
  segments,
  timing,
  elapsedSec,
  phase,
}: Props) {
  // Hooks run unconditionally to keep order stable across phase transitions.
  const idx = Math.max(0, currentSegmentIdx(elapsedSec, timing));
  const seg = segments[idx] as Segment | undefined;
  const t = timing[idx] ?? { start: 0, dur: 1 };
  const segElapsed = Math.max(0, elapsedSec - t.start);
  const isKaraoke =
    phase === "recording" && !!seg?.karaoke && t.dur > 0;

  // Capture wall-clock at segment start so we can interpolate sub-second
  // progress for the karaoke underline (parent only ticks at 1Hz).
  const segOriginWallMs = useRef(performance.now());
  const segOriginSegEl = useRef(0);
  useEffect(() => {
    segOriginWallMs.current = performance.now();
    segOriginSegEl.current = Math.max(0, elapsedSec - t.start);
    // We only want this to fire when the segment changes — capturing the
    // current elapsed at that moment. Re-running on every elapsedSec tick
    // would reset the wall clock and cause stutter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // RAF loop, active only while a karaoke segment is on screen.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isKaraoke) return;
    let raf = 0;
    const loop = () => {
      setTick((n) => (n + 1) & 0xffff);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [isKaraoke, idx]);

  const liveSegEl = isKaraoke
    ? segOriginSegEl.current +
      (performance.now() - segOriginWallMs.current) / 1000
    : segElapsed;
  const liveSegFrac =
    t.dur > 0 ? Math.min(1, Math.max(0, liveSegEl / t.dur)) : 0;

  // ── Intro mode (ready / countdown) ─────────────────────────────────
  if (phase !== "recording") {
    const lines = intro.length > 0 ? intro : segments.slice(0, 1).map((s) => s.raw);
    if (lines.length === 0) return null;
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 gap-3">
        {lines.map((line, i) => (
          <div
            key={i}
            className="text-xl md:text-3xl text-ivory/95 font-medium text-center leading-snug max-w-3xl whitespace-pre-line"
          >
            {renderLine(line)}
          </div>
        ))}
      </div>
    );
  }

  // ── Recording mode — active segment based on elapsed time ──────────
  if (segments.length === 0 || !seg) return null;
  const segmentProgress = t.dur > 0 ? Math.min(1, segElapsed / t.dur) : 1;

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 gap-4">
      {segments.length > 1 && (
        <div className="w-full max-w-md flex items-center gap-3">
          <span className="text-ivory/60 text-xs font-mono tabular-nums">
            {idx + 1}/{segments.length}
          </span>
          <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-lemonade transition-[width] duration-100 ease-linear"
              style={{ width: `${segmentProgress * 100}%` }}
            />
          </div>
        </div>
      )}
      <div
        key={idx}
        className="teleprompter-fade text-2xl md:text-4xl text-ivory font-semibold text-center leading-snug max-w-3xl whitespace-pre-line"
      >
        {isKaraoke
          ? renderKaraokeLine(seg.raw, liveSegFrac)
          : renderLine(seg.raw)}
      </div>
    </div>
  );
}

/** Render one line: emojify, then tokenize for placeholders + brackets. */
function renderLine(rawLine: string): ReactNode {
  const line = emojify(rawLine);
  const parts = line.split(SCRIPT_TOKEN_REGEX).filter(Boolean);
  return parts.map((part, i) => {
    const ph = part.match(PLACEHOLDER_INNER);
    if (ph) {
      return (
        <span key={i} className="text-lemonade font-bold">
          {ph[1]}
        </span>
      );
    }
    const br = part.match(BRACKET_INNER);
    if (br) {
      return (
        <span key={i} className="text-orange-400 font-bold">
          {br[1]}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

type KaraokeUnit =
  | { kind: "word"; text: string; color: "plain" | "lemonade" | "orange"; weight: number }
  | { kind: "space"; text: string };

/** Render with a per-word progressive lemonade underline that fills with
 *  `segFrac` (0..1). Words are weighted by character count so long words get
 *  more time than short ones — a natural reading cadence. Existing placeholder
 *  and bracket highlights are preserved. */
function renderKaraokeLine(rawLine: string, segFrac: number): ReactNode {
  const line = emojify(rawLine);
  const parts = line.split(SCRIPT_TOKEN_REGEX).filter(Boolean);

  const units: KaraokeUnit[] = [];
  const wordRe = /(\s+|\S+)/g;
  for (const part of parts) {
    const ph = part.match(PLACEHOLDER_INNER);
    const br = part.match(BRACKET_INNER);
    let color: "plain" | "lemonade" | "orange" = "plain";
    let inner = part;
    if (ph) {
      color = "lemonade";
      inner = ph[1];
    } else if (br) {
      color = "orange";
      inner = br[1];
    }
    wordRe.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = wordRe.exec(inner)) !== null) {
      const tok = m[0];
      if (/^\s+$/.test(tok)) {
        units.push({ kind: "space", text: tok });
      } else {
        units.push({
          kind: "word",
          text: tok,
          color,
          weight: Math.max(1, tok.length),
        });
      }
    }
  }

  const totalWeight =
    units.reduce((s, u) => s + (u.kind === "word" ? u.weight : 0), 0) || 1;
  let cum = 0;

  return units.map((u, i) => {
    if (u.kind === "space") {
      return <span key={i}>{u.text}</span>;
    }
    const startFrac = cum / totalWeight;
    cum += u.weight;
    const endFrac = cum / totalWeight;
    const range = Math.max(0.0001, endFrac - startFrac);
    const progress = Math.min(1, Math.max(0, (segFrac - startFrac) / range));
    const colorClass =
      u.color === "lemonade"
        ? "text-lemonade font-bold"
        : u.color === "orange"
        ? "text-orange-400 font-bold"
        : "";
    return (
      <span
        key={i}
        className={colorClass}
        style={{
          backgroundImage: `linear-gradient(${LEMONADE}, ${LEMONADE})`,
          backgroundPosition: "left bottom",
          backgroundRepeat: "no-repeat",
          backgroundSize: `${progress * 100}% 3px`,
          paddingBottom: "4px",
        }}
      >
        {u.text}
      </span>
    );
  });
}
